import { ErrorHandler, Injectable, InjectionToken, inject } from '@angular/core';

export const CHUNK_RELOAD_FN = new InjectionToken<() => void>('CHUNK_RELOAD_FN', {
  factory: () => () => window.location.reload(),
});

const RELOADED_AT_KEY = 'chunk-load-reloaded-at';
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_LOAD_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

/**
 * Recovers from stale-deploy chunk failures: when the client runs a build whose
 * lazy chunks were replaced by a newer deploy, reload once to pick up the new
 * index.html instead of leaving the user stuck on a broken route.
 */
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  private readonly reload = inject(CHUNK_RELOAD_FN);

  override handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.markReloaded()) {
      this.reload();
      return;
    }
    super.handleError(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.messageOf(error);
    return CHUNK_LOAD_MESSAGES.some((pattern) => message.includes(pattern));
  }

  private messageOf(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return typeof error === 'string' ? error : '';
  }

  private markReloaded(): boolean {
    try {
      const reloadedAt = Number(sessionStorage.getItem(RELOADED_AT_KEY));
      if (reloadedAt && Date.now() - reloadedAt <= RELOAD_COOLDOWN_MS) {
        return false;
      }
      sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
      return true;
    } catch {
      // sessionStorage unavailable (private-mode iOS, etc.) — without loop
      // protection an auto-reload could cycle forever, so let the error surface
      return false;
    }
  }
}
